import { FC, useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { 
  TableBody, 
  TableHead, 
  TableRow, 
  Box, 
  Typography, 
  useTheme, 
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import {
  StyledTableContainer,
  HeaderCell,
  SubHeaderCell,
  StyledPaper,
  StyledTable,
  DataCell,
  TableWrapper,
  TableTitle,
  debounce,
  calculateTextWidth
} from '../../../../common/components/StatusTable/StatusTable.styled';
import type { SortConfig, SortDirection } from '../../../../common/components/Table/types';

interface E55DataDto {
  sendingOe: string;
  receivingOe: string;
  mailCategory: string;
  format: string;
  netOrGross: string;
  e55Weight: number | string | null;
}

interface Eno1DataDto {
  sendingOe: string;
  receivingOe: string;
  mailCategory: string;
  format: string;
  netOrGross: string;
  quarter: number;
  receptacleCategoryIncluded: boolean;
  receivedWeightStratum: number | string;
  stratum: string;
}

interface ComparisonResult {
  e55DataDto: E55DataDto | null;
  eno1DataDto: Eno1DataDto | null;
  difference: {
    kgDifference: string | null;
    percentageDifference: string | null;
  };
  year: number;
  quarter: number;
}

interface E55ComparisonData {
  quarter: number;
  year: number;
  eno1Accepted: string | null;
  e55Accepted: string | null;
  compareResult: ComparisonResult[];
  filename?: string;
  designatedOperator?: string;
  eno1?: string | null;
  e55?: string | null;
}

interface E55ComparisonTableProps {
  data: E55ComparisonData[];
  title?: string;
  backgroundColor?: string;
  direction?: 'Inbound' | 'Outbound' ;
}

// Helper function to check if a file is accepted
const isAccepted = (status: string | null): boolean => {
  return status === 'accepted';
};

// Section Header Component for each period
const PeriodSectionHeader: FC<{
  period: E55ComparisonData;
}> = ({ period }) => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Period Title */}
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 700, 
          color: '#063142',
          fontSize: '18px',
          mb: 2
        }}
      >
        DO: {period.designatedOperator || 'N/A'} - Accounting Period: Q{period.quarter}.{period.year}
      </Typography>
      
      {/* Files Section */}
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600, 
            color: '#063142',
            fontSize: '14px',
            mb: 1
          }}
        >
          Files:
        </Typography>
        
        {/* Display file information */}
        <Box>
          {/* E55 filename */}
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '12px',
              color: '#666',
              fontFamily: 'monospace',
              mb: 0.5
            }}
          >
            E55: {period.e55 || 'no data available'} 
            {period.e55 && (
              <Box 
                component="span" 
                sx={{ 
                  color: isAccepted(period.e55Accepted) ? '#2e7d32' : '#d32f2f',
                  fontWeight: 600
                }}
              >
                {isAccepted(period.e55Accepted) ? ' (accepted)' : ' (not accepted)'}
              </Box>
            )}
          </Typography>
          
          {/* eNO1 filename */}
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '12px',
              color: '#666',
              fontFamily: 'monospace'
            }}
          >
            eNO1: {period.eno1 || 'no data available'} 
            {period.eno1 && (
              <Box 
                component="span" 
                sx={{ 
                  color: isAccepted(period.eno1Accepted) ? '#2e7d32' : '#d32f2f',
                  fontWeight: 600
                }}
              >
                {isAccepted(period.eno1Accepted) ? ' (accepted)' : ' (not accepted)'}
              </Box>
            )}
          </Typography>
          
          {/* Show error message if any filename is missing */}
          {(!period.e55 || !period.eno1) && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '14px',
                color: '#d32f2f',
                fontStyle: 'italic',
                mb: 1,
                mt: 1
              }}
            >
              No comparison data available for the selected filter criteria.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Individual Period Table Component with sorting
const PeriodTable: FC<{
  period: E55ComparisonData;
  columnWidths: Record<string, string>;
  backgroundColor?: string;
  direction?: 'Inbound' | 'Outbound';
  sortConfig?: SortConfig;
  onSort?: (column: string, direction: SortDirection | undefined) => void;
}> = ({ period, columnWidths, backgroundColor, direction, sortConfig, onSort }) => {
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);

  // If no comparison results, don't show the table
  if (!period.compareResult || period.compareResult.length === 0) {
    return null;
  }

  const handleSortClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setActiveColumn(column);
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortSelect = (direction: SortDirection) => {
    if (activeColumn && onSort) {
      // Check if we're clicking the same sort direction - if so, disable sorting
      if (sortConfig && sortConfig.column === activeColumn && sortConfig.direction === direction) {
        // Disable sorting by calling onSort with undefined direction
        onSort(activeColumn, undefined as any);
      } else {
        // Apply new sorting
        onSort(activeColumn, direction);
      }
    }
    setSortAnchorEl(null);
  };

  // Helper function to parse numeric values with thousand separators
  const parseNumeric = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      // Remove thousand separators (apostrophes, backticks, commas, spaces) and parse
      const cleaned = value.replace(/['`,\s]/g, '');
      const num = Number(cleaned);
      if (Number.isFinite(num)) return num;
    }
    return null;
  };

  // Sort the comparison results based on sortConfig
  const sortedResults = useMemo(() => {
    if (!sortConfig) return period.compareResult;

    const { column, direction: sortDirection } = sortConfig;
    const multiplier = sortDirection === 'ASC' ? 1 : -1;

    return [...period.compareResult].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Map column names to actual data properties
      switch (column) {
        case 'year':
          aValue = a.year;
          bValue = b.year;
          break;
        case 'quarter':
          aValue = a.quarter;
          bValue = b.quarter;
          break;
        case 'mailCategory':
          aValue = a.e55DataDto?.mailCategory || '';
          bValue = b.e55DataDto?.mailCategory || '';
          break;
        case 'format':
          aValue = a.e55DataDto?.format || '';
          bValue = b.e55DataDto?.format || '';
          break;
        case 'sendingOe':
          aValue = a.e55DataDto?.sendingOe || '';
          bValue = b.e55DataDto?.sendingOe || '';
          break;
        case 'receivingOe':
          aValue = a.e55DataDto?.receivingOe || '';
          bValue = b.e55DataDto?.receivingOe || '';
          break;
        case 'netOrGross':
          aValue = a.e55DataDto?.netOrGross || '';
          bValue = b.e55DataDto?.netOrGross || '';
          break;
        case 'e55Weight':
          aValue = a.e55DataDto?.e55Weight;
          bValue = b.e55DataDto?.e55Weight;
          break;
        case 'stratum':
          aValue = a.eno1DataDto?.stratum || '';
          bValue = b.eno1DataDto?.stratum || '';
          break;
        case 'receptacleCategoryIncluded':
          aValue = a.eno1DataDto?.receptacleCategoryIncluded ? 'Yes' : 'No';
          bValue = b.eno1DataDto?.receptacleCategoryIncluded ? 'Yes' : 'No';
          break;
        case 'receivedWeightStratum':
          aValue = a.eno1DataDto?.receivedWeightStratum;
          bValue = b.eno1DataDto?.receivedWeightStratum;
          break;
        case 'kgDifference':
          aValue = a.difference.kgDifference;
          bValue = b.difference.kgDifference;
          break;
        case 'percentageDifference':
          aValue = a.difference.percentageDifference;
          bValue = b.difference.percentageDifference;
          break;
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return -1 * multiplier;
      if (bValue == null) return 1 * multiplier;

      // Try numeric comparison first (handles thousand separators)
      const aNum = parseNumeric(aValue);
      const bNum = parseNumeric(bValue);
      
      if (aNum !== null && bNum !== null) {
        return (aNum - bNum) * multiplier;
      }

      // String comparison for non-numeric values
      const aStr = String(aValue);
      const bStr = String(bValue);
      return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
  }, [period.compareResult, sortConfig]);

  return (
    <StyledPaper sx={{ mb: 4 }}>
      <StyledTableContainer>
        <StyledTable size='small' aria-label='e55 comparison table' sx={{ borderCollapse: 'separate', borderSpacing: '1px' }}>
          <TableHead>
            {/* First header row - Main column groups */}
            <TableRow sx={{ 
              '& th': { 
                px: 1.5, 
                py: 1,
                position: 'sticky',
                top: 0,
                zIndex: 1300,
                backgroundColor: '#E7EFF5 !important',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              } 
            }}>
              <HeaderCell align='center' rowSpan={2} width={columnWidths['year']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Year
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'year')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'year' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'year' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'year' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'year' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </HeaderCell>
              <HeaderCell align='center' rowSpan={2} width={columnWidths['quarter']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Quarter
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'quarter')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'quarter' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'quarter' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'quarter' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'quarter' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </HeaderCell>
              <HeaderCell align='center' colSpan={6}>E55 Data</HeaderCell>
              <HeaderCell align='center' colSpan={3}>eNO1 Data</HeaderCell>
              <HeaderCell align='center' rowSpan={2} width={columnWidths['kgDifference']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Difference (kg)
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'kgDifference')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'kgDifference' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'kgDifference' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'kgDifference' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'kgDifference' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </HeaderCell>
              <HeaderCell align='center' rowSpan={2} width={columnWidths['percentageDifference']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Difference (%)
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'percentageDifference')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'percentageDifference' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'percentageDifference' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'percentageDifference' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'percentageDifference' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </HeaderCell>
            </TableRow>
            
            {/* Second header row - Sub columns */}
            <TableRow>
              {/* E55 Data sub-columns */}
              <SubHeaderCell width={columnWidths['mailCategory']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Mail Category
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'mailCategory')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'mailCategory' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'mailCategory' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'mailCategory' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'mailCategory' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
              <SubHeaderCell width={columnWidths['format']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Format
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'format')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'format' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'format' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'format' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'format' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
              <SubHeaderCell width={columnWidths['sendingOe']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Sending OE
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'sendingOe')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'sendingOe' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'sendingOe' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'sendingOe' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'sendingOe' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
              <SubHeaderCell width={columnWidths['receivingOe']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Receiving OE
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'receivingOe')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'receivingOe' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'receivingOe' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'receivingOe' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'receivingOe' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
              <SubHeaderCell width={columnWidths['netOrGross']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Net/Gross
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'netOrGross')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'netOrGross' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'netOrGross' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'netOrGross' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'netOrGross' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
              <SubHeaderCell width={columnWidths['e55Weight']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  e55 Weight
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'e55Weight')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'e55Weight' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'e55Weight' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'e55Weight' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'e55Weight' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
              
              {/* eNO1 Data sub-columns */}
              <SubHeaderCell width={columnWidths['stratum']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Stratum (without rec. cat.)
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'stratum')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'stratum' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'stratum' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'stratum' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'stratum' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
              <SubHeaderCell width={columnWidths['receptacleCategoryIncluded']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Rec. cat.<br/>included in<br/>eNO1 strata
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'receptacleCategoryIncluded')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'receptacleCategoryIncluded' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'receptacleCategoryIncluded' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'receptacleCategoryIncluded' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'receptacleCategoryIncluded' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
              <SubHeaderCell width={columnWidths['receivedWeightStratum']}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                  Received Weight Stratum
                  {onSort && (
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, 'receivedWeightStratum')}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: sortConfig && sortConfig.column === 'receivedWeightStratum' ? '#E3F2FD !important' : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: '2px',
                        border: sortConfig && sortConfig.column === 'receivedWeightStratum' ? '1px solid #2196F3' : 'none',
                        '&:hover': {
                          backgroundColor: sortConfig && sortConfig.column === 'receivedWeightStratum' ? '#FFEBEE !important' : '#E3F2FD !important',
                        },
                      }}
                    >
                      {sortConfig && sortConfig.column === 'receivedWeightStratum' ? (
                        sortConfig.direction === 'ASC' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
                        )
                      ) : (
                        <MoreVertIcon sx={{ fontSize: 12 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </SubHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResults.map((row, index) => {
              // Determine background color based on direction
              let cellBackgroundColor = backgroundColor;
              if (direction === 'Inbound') {
                cellBackgroundColor = '#f1f8e9'; // Light green for inbound
              } else if (direction === 'Outbound') {
                cellBackgroundColor = '#ffebee'; // Light red for outbound
              }
              
              // Create base cell props with background color
              const baseCellProps = cellBackgroundColor ? { sx: { backgroundColor: cellBackgroundColor } } : {};
              
              // Check if we have missing data
              const hasE55Data = row.e55DataDto !== null;
              const hasEno1Data = row.eno1DataDto !== null;
              
              return (
                <TableRow key={index} sx={{ '& td': { px: 1.5, py: 0.75 } }}>
                  {/* Year and Quarter */}
                  <DataCell {...baseCellProps}>{row.year}</DataCell>
                  <DataCell {...baseCellProps}>{row.quarter}</DataCell>
                  
                  {/* E55 Data columns */}
                  {hasE55Data ? (
                    // Check if both sending and receiving OE are null/empty
                    (!row.e55DataDto!.sendingOe && !row.e55DataDto!.receivingOe) ? (
                      <DataCell 
                        {...baseCellProps} 
                        colSpan={6} 
                        sx={{ 
                          ...baseCellProps.sx, 
                          textAlign: 'center', 
                          fontStyle: 'italic', 
                          color: '#999' 
                        }}
                      >
                        No data available
                      </DataCell>
                    ) : (
                      <>
                        <DataCell {...baseCellProps}>{row.e55DataDto!.mailCategory}</DataCell>
                        <DataCell {...baseCellProps}>{row.e55DataDto!.format}</DataCell>
                        <DataCell {...baseCellProps}>{row.e55DataDto!.sendingOe}</DataCell>
                        <DataCell {...baseCellProps}>{row.e55DataDto!.receivingOe}</DataCell>
                        <DataCell {...baseCellProps}>{row.e55DataDto!.netOrGross}</DataCell>
                        <DataCell {...baseCellProps} sx={{ ...baseCellProps.sx, textAlign: 'right' }}>{row.e55DataDto!.e55Weight ?? ''}</DataCell>
                      </>
                    )
                  ) : (
                    <DataCell 
                      {...baseCellProps} 
                      colSpan={6} 
                      sx={{ 
                        ...baseCellProps.sx, 
                        textAlign: 'center', 
                        fontStyle: 'italic', 
                        color: '#999' 
                      }}
                    >
                      No data available
                    </DataCell>
                  )}
                  
                  {/* eNO1 Data columns */}
                  {hasEno1Data ? (
                    <>
                      <DataCell {...baseCellProps}>{row.eno1DataDto!.stratum}</DataCell>
                      <DataCell {...baseCellProps}>{row.eno1DataDto!.receptacleCategoryIncluded ? 'Yes' : 'No'}</DataCell>
                      <DataCell {...baseCellProps} sx={{ ...baseCellProps.sx, textAlign: 'right' }}>{row.eno1DataDto!.receivedWeightStratum ?? ''}</DataCell>
                    </>
                  ) : (
                    <DataCell 
                      {...baseCellProps} 
                      colSpan={3} 
                      sx={{ 
                        ...baseCellProps.sx, 
                        textAlign: 'center', 
                        fontStyle: 'italic', 
                        color: '#999' 
                      }}
                    >
                      No data available
                    </DataCell>
                  )}
                  
                  {/* Difference columns */}
                  <DataCell {...baseCellProps} sx={{ ...baseCellProps.sx, textAlign: 'right' }}>{row.difference.kgDifference ?? ''}</DataCell>
                  <DataCell {...baseCellProps} sx={{ ...baseCellProps.sx, textAlign: 'right' }}>{row.difference.percentageDifference ?? ''}</DataCell>
                </TableRow>
              );
            })}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      
      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleSortSelect('ASC')}>
          <ArrowUpwardIcon sx={{ mr: 1 }} />
          Sort Ascending
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect('DESC')}>
          <ArrowDownwardIcon sx={{ mr: 1 }} />
          Sort Descending
        </MenuItem>
        
        {/* Clear Sort Option - only show if there's an active sort on this column */}
        {sortConfig && sortConfig.column === activeColumn && (
          <>
            <Divider />
            <MenuItem 
              onClick={() => handleSortSelect(sortConfig.direction)}
              sx={{
                backgroundColor: 'rgba(244, 67, 54, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                }
              }}
            >
              <CloseIcon sx={{ color: '#F44336', fontSize: 16, mr: 1 }} />
              Clear Sort
            </MenuItem>
          </>
        )}
      </Menu>
    </StyledPaper>
  );
};

// Sorted wrapper component that manages sort state
const SortedE55ComparisonTable: FC<E55ComparisonTableProps> = ({ 
  data, 
  title = "",
  backgroundColor,
  direction
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);

  const handleSort = useCallback((column: string, direction: SortDirection | undefined) => {
    if (!direction) {
      setSortConfig(undefined);
      return;
    }
    setSortConfig({ column, direction });
  }, []);

  return (
    <E55ComparisonTable 
      data={data}
      title={title}
      backgroundColor={backgroundColor}
      direction={direction}
      sortConfig={sortConfig}
      onSort={handleSort}
    />
  );
};

export const E55ComparisonTable: FC<E55ComparisonTableProps & {
  sortConfig?: SortConfig;
  onSort?: (column: string, direction: SortDirection | undefined) => void;
}> = ({ 
  data, 
  title = "",
  backgroundColor,
  direction,
  sortConfig,
  onSort
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [isCalculating, setIsCalculating] = useState(true);

  // Flatten the data for width calculation
  const flattenedData = data.flatMap(period => 
    period.compareResult.map(result => ({
      ...result,
      periodYear: period.year,
      periodQuarter: period.quarter,
      eno1Accepted: isAccepted(period.eno1Accepted),
      e55Accepted: isAccepted(period.e55Accepted)
    }))
  );

  // Calculate optimal column widths based on content
  const calculateColumnWidths = () => {
    if (!tableContainerRef.current) {
      setIsCalculating(false);
      return;
    }

    const newColumnWidths: Record<string, number> = {};
    const headerFont = '700 12px "Lato", sans-serif';
    const cellFont = '400 12px "Lato", sans-serif';

    // Define min and max column widths
    const minColWidth = isMobile ? 50 : 70;
    const maxColWidth = isMobile ? 120 : 150;

    // Initialize column widths with header text
    const columns = [
      'year', 'quarter', 'mailCategory', 'format', 'sendingOe', 'receivingOe', 
      'netOrGross', 'e55Weight', 'stratum', 'receptacleCategoryIncluded', 
      'receivedWeightStratum', 'kgDifference', 'percentageDifference'
    ];

    columns.forEach(col => {
      newColumnWidths[col] = minColWidth;
    });

    // Calculate based on header text
    newColumnWidths['year'] = Math.max(calculateTextWidth('Year', headerFont) + 16, minColWidth);
    newColumnWidths['quarter'] = Math.max(calculateTextWidth('Quarter', headerFont) + 16, minColWidth);
    newColumnWidths['mailCategory'] = Math.max(calculateTextWidth('Mail Category', headerFont) + 16, minColWidth);
    newColumnWidths['format'] = Math.max(calculateTextWidth('Format', headerFont) + 16, minColWidth);
    newColumnWidths['sendingOe'] = Math.max(calculateTextWidth('Sending OE', headerFont) + 16, minColWidth);
    newColumnWidths['receivingOe'] = Math.max(calculateTextWidth('Receiving OE', headerFont) + 16, minColWidth);
    newColumnWidths['netOrGross'] = Math.max(calculateTextWidth('Net/Gross', headerFont) + 16, minColWidth);
    newColumnWidths['e55Weight'] = Math.max(calculateTextWidth('e55 Weight', headerFont) + 16, minColWidth);
    newColumnWidths['stratum'] = Math.max(calculateTextWidth('Stratum (without rec. cat.)', headerFont) + 24, minColWidth);
    newColumnWidths['receptacleCategoryIncluded'] = Math.max(calculateTextWidth('included in', headerFont) + 16, minColWidth);
    newColumnWidths['receivedWeightStratum'] = Math.max(calculateTextWidth('Received Weight Stratum', headerFont) + 16, minColWidth);
    newColumnWidths['kgDifference'] = Math.max(calculateTextWidth('Difference (kg)', headerFont) + 16, minColWidth);
    newColumnWidths['percentageDifference'] = Math.max(calculateTextWidth('Difference (%)', headerFont) + 16, minColWidth);

    // Sample data to get better width estimates (only if we have flattened data)
    if (flattenedData.length > 0) {
      flattenedData.slice(0, 10).forEach(row => {
        newColumnWidths['year'] = Math.max(
          newColumnWidths['year'], 
          calculateTextWidth(String(row.periodYear), cellFont) + 16
        );
        newColumnWidths['quarter'] = Math.max(
          newColumnWidths['quarter'], 
          calculateTextWidth(`Q${row.periodQuarter}`, cellFont) + 16
        );
        newColumnWidths['mailCategory'] = Math.max(
          newColumnWidths['mailCategory'], 
          calculateTextWidth(String(row.e55DataDto?.mailCategory || ''), cellFont) + 16
        );
        newColumnWidths['format'] = Math.max(
          newColumnWidths['format'], 
          calculateTextWidth(String(row.e55DataDto?.format || ''), cellFont) + 16
        );
        newColumnWidths['sendingOe'] = Math.max(
          newColumnWidths['sendingOe'], 
          calculateTextWidth(String(row.e55DataDto?.sendingOe || ''), cellFont) + 16
        );
        newColumnWidths['receivingOe'] = Math.max(
          newColumnWidths['receivingOe'], 
          calculateTextWidth(String(row.e55DataDto?.receivingOe || ''), cellFont) + 16
        );
        newColumnWidths['netOrGross'] = Math.max(
          newColumnWidths['netOrGross'], 
          calculateTextWidth(String(row.e55DataDto?.netOrGross || ''), cellFont) + 16
        );
        newColumnWidths['e55Weight'] = Math.max(
          newColumnWidths['e55Weight'], 
          calculateTextWidth(String(row.e55DataDto?.e55Weight || ''), cellFont) + 16
        );
        newColumnWidths['stratum'] = Math.max(
          newColumnWidths['stratum'], 
          calculateTextWidth(String(row.eno1DataDto?.stratum || ''), cellFont) + 16
        );
        newColumnWidths['receptacleCategoryIncluded'] = Math.max(
          newColumnWidths['receptacleCategoryIncluded'], 
          calculateTextWidth(String(row.eno1DataDto?.receptacleCategoryIncluded ? 'Yes' : 'No'), cellFont) + 16
        );
        newColumnWidths['receivedWeightStratum'] = Math.max(
          newColumnWidths['receivedWeightStratum'], 
          calculateTextWidth(String(row.eno1DataDto?.receivedWeightStratum || ''), cellFont) + 16
        );
        newColumnWidths['kgDifference'] = Math.max(
          newColumnWidths['kgDifference'], 
          calculateTextWidth(String(row.difference.kgDifference || ''), cellFont) + 16
        );
        newColumnWidths['percentageDifference'] = Math.max(
          newColumnWidths['percentageDifference'], 
          calculateTextWidth(String(row.difference.percentageDifference || ''), cellFont) + 16
        );
      });
    }

    // Apply min/max constraints
    Object.keys(newColumnWidths).forEach(key => {
      newColumnWidths[key] = Math.min(Math.max(newColumnWidths[key], minColWidth), maxColWidth);
    });

    // Convert numeric widths to strings with 'px' suffix
    const finalColumnWidths: Record<string, string> = {};
    for (const [key, value] of Object.entries(newColumnWidths)) {
      finalColumnWidths[key] = `${value}px`;
    }

    setColumnWidths(finalColumnWidths);
    setIsCalculating(false);
  };

  // Debounced version of the calculation function
  const debouncedCalculateColumnWidths = debounce(calculateColumnWidths, 300);

  // Calculate column widths on initial render and when data changes
  useEffect(() => {
    setIsCalculating(true);
    calculateColumnWidths();
  }, [data]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsCalculating(true);
      debouncedCalculateColumnWidths();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Sort data by year and quarter (newest first)
  const sortedData = [...data].sort((a, b) => {
    if (a.year !== b.year) {
      return b.year - a.year; // Newest year first
    }
    return b.quarter - a.quarter; // Newest quarter first
  });

  return (
    <TableWrapper>
      <TableTitle>
        {title}
      </TableTitle>
      
      <Box ref={tableContainerRef}>
        {sortedData.map((period, index) => (
          <Box key={`${period.year}-Q${period.quarter}`} sx={{ mb: 4 }}>
            {/* Period Section Header */}
            <PeriodSectionHeader period={period} />
            
            {/* Period Table (only show if there's data) */}
            <PeriodTable 
              period={period}
              columnWidths={columnWidths}
              backgroundColor={backgroundColor}
              direction={direction}
              sortConfig={sortConfig}
              onSort={onSort}
            />
          </Box>
        ))}
      </Box>
      
      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: '0.75rem', color: '#666' }}>
        E55 vs eNO1 data comparison showing differences in weight measurements between systems
      </Typography>
    </TableWrapper>
  );
};

// Export the sorted wrapper as the default export
export default SortedE55ComparisonTable; 